import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatButtonToggle,
  MatButtonToggleChange,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { BatteryIconComponent } from '@components/shared/battery-icon/battery-icon.component';
import { DatetimeRangePickerComponent } from '@components/shared/datetime-range-picker/datetime-range-picker.component';
import { SOURCE_PAGE_QUERY_PARAM, SourcePage } from '@entities/shipment';
import numeral from 'numeral';

/** 計測種別 */
export type SensingType =
  | 'temperature'
  | 'humidity'
  | 'pressure'
  | 'light'
  | 'tilt'
  | 'fallDetected'
  | 'mcdDetected'
  | 'lightExposure'
  | 'shockDetected'
  | 'battery';

/** サマリーボタン */
export type summaryBtnOption = {
  type: 'sensor' | 'event' | 'battery';
  label: string;
  iconName: string;
  unit: string;
  key: SensingType;
};

/** 一つのセンサの計測結果のViewModel */
export type SensingViewModel = {
  status?: string;
  value?: number;
  eventAt: string;
  activate?: boolean;
  eventType?: string;
  eventTrigger?: string;
  threshold?: number;
};

export type LocationModel = {
  longitude: number;
  latitude: number;
  eventAt: string;
  address?: string;
  timeStamp?: number | null;
  locationTechType?: string | null;
  radioAccessTechnology?: string | null;
};

export type LocationSelectionViewModel = {
  locationTimeStamp: number;
  longitude: number;
  latitude: number;
};

export type ChartPointViewModel = SensingViewModel | LocationSelectionViewModel;

export type BatteryHistoryViewModel = {
  batteryPercentage: number;
  deviceReportedEpochtime: number;
};

/** 複数センサの計測結果のViewModel */
export type DeviceInfoViewModel = {
  /** 温度 */
  temperature?: SensingViewModel;
  /** 気圧 */
  pressure?: SensingViewModel;
  /** 湿度 */
  humidity?: SensingViewModel;
  /** 傾斜 */
  tilt?: SensingViewModel;
  /** 照度 */
  light?: SensingViewModel;
  /** 落下検知 */
  fallDetected?: SensingViewModel;
  /** 動作検知 */
  mcdDetected?: SensingViewModel;
  /** 明暗検知 */
  lightExposure?: SensingViewModel;
  /** 衝撃検知 */
  shockDetected?: SensingViewModel;
  /** バッテリー残量 */
  batteryPercentage?: number | null;
  /** デバイス報告日時（エポックミリ秒） */
  deviceReportedEpochtime?: number | null;
  /** 位置情報 */
  location?: LocationModel | null;
};

/** エクスポートのcsvデータモデル */
export type DeviceInfoCsv = {
  serverReceivedTimeStamp?: number;
  deviceReportingTimeStamp?: number;
  temperature?: number;
  temperatureAlertType?: string;
  humidity?: number;
  humidityAlertType?: string;
  pressure?: number;
  pressureAlertType?: string;
  light?: number;
  lightAlertType?: string;
  tilt?: number;
  tiltAlertType?: string;
  latitude?: number;
  longitude?: number;
  locationTechType?: string;
  batteryPercentage?: string;
};

/** エクスポートのレスポンスのデータモデル */
export type DeviceInfoExportHistory = {
  serverReceivedEpochtime: number;
  deviceReportedEpochtime: number;
  temperature?: number;
  temperatureEventType?: string;
  humidity?: number;
  humidityEventType?: string;
  pressure?: number;
  pressureEventType?: string;
  light?: number;
  lightEventType?: string;
  tilt?: number;
  tiltEventType?: string;
  latitude?: number;
  longitude?: number;
  address?: string;
  locationTechType?: string;
  batteryPercentage?: string;
  status?: string;
  fallDetectedEventType?: string;
  mcdDetectedEventType?: string;
  lightExposureEventType?: string;
  shockDetectedEventType?: string;
  alertTimeStamp: number;
  serialNumber: string;
};

/** 一つのセンサの設定のViewModel */
export type SensorSettingViewModel = {
  availability: boolean; // 有/無
  enabled: boolean; // ON/OFF
};

/** 複数センサの設定のViewModel */
export type DeviceSettingViewModel = {
  deviceName?: string;
  temperature?: SensorSettingViewModel;
  pressure?: SensorSettingViewModel;
  humidity?: SensorSettingViewModel;
  tilt?: SensorSettingViewModel;
  light?: SensorSettingViewModel;
  fallDetected?: SensorSettingViewModel;
  mcdDetected?: SensorSettingViewModel;
  lightExposure?: SensorSettingViewModel;
  shockDetected?: SensorSettingViewModel;
};

@Component({
  selector: 'app-device-detail-summary',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatDatepickerModule,
    MatInputModule,
    MatButtonToggle,
    MatButtonToggleGroup,
    DatetimeRangePickerComponent,
    BatteryIconComponent,
  ],
  templateUrl: './device-detail-summary.component.html',
  styleUrl: './device-detail-summary.component.scss',
})
export class DeviceDetailSummaryComponent implements OnChanges, OnInit {
  /** デバイス最新情報 */
  @Input({ required: true }) public latestDeviceInfo: DeviceInfoViewModel = {};

  /** エクスポート用のデバイス履歴情報 */
  @Input() public deviceInfoExportHistory: DeviceInfoExportHistory[] = [];

  /** デバイス設定 */
  @Input() public deviceSetting: DeviceSettingViewModel = {};

  /** デバイス名 */
  @Input() public deviceName: string = '';

  /** バッテリー */
  @Input() public batteryPercentage: number | null = null;

  /** 検索期間内の最新バッテリー */
  @Input() public historyBatteryPercentage: number | null = null;

  /** 検索期間内の最新バッテリー日時 */
  @Input() public historyBatteryReportedAt: Date | null = null;

  /** 検索期間内に低下したバッテリーがあるか */
  @Input() public batteryLow = false;

  /** 最終更新日時 */
  @Input() public lastCommunicationAt: string = '';

  /** フライトモードのON/OFF */
  @Input() public fmdOn?: boolean | null;

  /** フライトモードの有効/無効 */
  @Input() public fmdEnabled?: boolean | null;

  /** 検索フォーム */
  @Input() public historyFormGroup: FormGroup | undefined;

  /** テナント作成日時 */
  @Input() public tenantCreatedAt: string | null = '';

  /** 削除を通知 */
  @Output() public historyFiltered = new EventEmitter<void>();

  /** エクスポートボタンクリック時にデータを取得 */
  @Output() public exportButtonClicked = new EventEmitter<void>();

  /** 計測選択を通知 */
  @Output() public sensingTypeChanged = new EventEmitter<SensingType | ''>();

  /** ボタン種別 */
  public selectedBtn: SensingType | '' = 'temperature';

  /** 各種ボタン設定 */
  public readonly summaryBtnOptions: summaryBtnOption[] = [
    {
      key: 'temperature',
      type: 'sensor',
      label: '温度',
      iconName: 'thermostat',
      unit: '℃',
    },
    {
      key: 'humidity',
      type: 'sensor',
      label: '湿度',
      iconName: 'water_drop',
      unit: '%',
    },
    {
      key: 'pressure',
      type: 'sensor',
      label: '気圧',
      iconName: 'speed',
      unit: 'hPa',
    },
    {
      key: 'light',
      type: 'sensor',
      label: '照度',
      iconName: 'wb_sunny',
      unit: 'lx',
    },
    {
      key: 'tilt',
      type: 'sensor',
      label: '傾斜',
      iconName: 'rotate_90_degrees_ccw',
      unit: '°',
    },
    {
      key: 'fallDetected',
      type: 'event',
      label: '落下',
      iconName: 'airline_stops',
      unit: '',
    },
    {
      key: 'mcdDetected',
      type: 'event',
      label: '動作',
      iconName: 'motion_photos_on',
      unit: '',
    },
    {
      key: 'lightExposure',
      type: 'event',
      label: '明暗',
      iconName: 'contrast',
      unit: '',
    },
    {
      key: 'shockDetected',
      type: 'event',
      label: '衝撃',
      iconName: 'flash_on',
      unit: '',
    },
    {
      key: 'battery',
      type: 'battery',
      label: '電源',
      iconName: 'battery_full',
      unit: '%',
    },
  ];

  /** 遷移元画面 */
  private previousPage = '';

  /** 遷移元画面から渡された配送ID */
  private previousShipmentId = '';
  /** 配送実績の対象期間（開始） */
  private previousResultHistoryFrom = '';
  /** 配送実績の対象期間（終了） */
  private previousResultHistoryTo = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // 必要な情報が揃ってから初期アクティブボタンを判定する
    const deviceChanged =
      changes['latestDeviceInfo'] !== undefined ||
      changes['deviceSetting'] !== undefined ||
      changes['historyBatteryPercentage'] !== undefined;
    const existedDeviceInfo =
      Object.keys(this.latestDeviceInfo).length > 0 &&
      Object.keys(this.deviceSetting).length > 0;
    const canSelectSummary =
      existedDeviceInfo || this.historyBatteryPercentage != null;
    if (deviceChanged && canSelectSummary) {
      // 表示対象かつ履歴内に最新データがある項目のうち、一番左を初期選択にする
      this.selectedBtn = '';
      for (const btnOption of this.summaryBtnOptions) {
        const key = btnOption.key;
        if (this.isAvailability(key) && this.hasSensingData(key)) {
          this.selectedBtn = key;
          break;
        }
      }
      this.sensingTypeChanged.emit(this.selectedBtn);
    }
  }

  ngOnInit(): void {
    // クエリパラメータから遷移元画面を判断する
    this.route.queryParams.pipe().subscribe((params) => {
      const pageQueryParam: string = params['page'];
      const shipmentIdQueryParam: string = params['shipmentId'];
      const resultHistoryFromQueryParam: string =
        params['resultHistoryFrom'] ?? '';
      const resultHistoryToQueryParam: string = params['resultHistoryTo'] ?? '';

      // 遷移元画面がダッシュボード画面、デバイス情報画面（デバイス一覧、地図）の場合
      if (
        ['dashboard', 'device-info', 'shipment-info'].includes(pageQueryParam)
      ) {
        this.previousPage = pageQueryParam;
        this.previousShipmentId = shipmentIdQueryParam;
        this.previousResultHistoryFrom = resultHistoryFromQueryParam;
        this.previousResultHistoryTo = resultHistoryToQueryParam;

        // 想定しないパラメータの場合はルートの画面に遷移させる
      } else {
        this.previousPage = '';
      }
    });
  }

  public getSensingValue(key: SensingType, unit: string): string | null {
    if (key === 'battery') {
      return this.getBatteryValue(unit, this.historyBatteryPercentage) || '-';
    }
    const deviceInfo = this.latestDeviceInfo ?? {};
    const value = deviceInfo[key]?.value;
    if (value != null) {
      const formattedValue = numeral(value).format('0.0');
      return `${formattedValue} ${unit}`;
    } else {
      return '-';
    }
  }

  public getSensingStatus(
    key: SensingType,
    type: 'sensor' | 'event' | 'battery',
  ): string {
    if (key === 'battery') {
      return this.batteryLow ? 'Alert' : 'normal';
    }
    const deviceInfo = this.latestDeviceInfo ?? {};

    if (type === 'sensor') {
      const status = deviceInfo[key]?.status;
      return status || 'unknown';
    } else {
      const eventAt = deviceInfo[key]?.eventAt;
      return eventAt ? 'Alert' : 'normal';
    }
  }

  public getBatteryValue(unit: string, value: number | null): string {
    if (value != null) {
      const formattedValue = numeral(value).format('0');
      return `${formattedValue} ${unit}`;
    } else {
      return '';
    }
  }

  public getSensingDate(key: SensingType): string {
    if (key === 'battery') {
      return this.historyBatteryReportedAt?.toISOString().split('T')[0] ?? '';
    }
    const deviceInfo = this.latestDeviceInfo ?? {};
    const eventAt = deviceInfo[key]?.eventAt;
    const eventAtList = eventAt?.split('T');
    return eventAtList && eventAtList.length > 0 ? eventAtList[0] : '';
  }

  public getSensingDateTime(key: SensingType): string {
    if (key === 'battery') {
      return this.historyBatteryReportedAt?.toISOString() ?? '';
    }
    const deviceInfo = this.latestDeviceInfo ?? {};
    return deviceInfo[key]?.eventAt ?? '';
  }

  public onSensingBtnChanged(event: MatButtonToggleChange) {
    this.sensingTypeChanged.emit(event.value);
  }

  public isAvailability(key: SensingType): boolean {
    if (key === 'battery') {
      return Object.keys(this.deviceSetting).length > 0;
    }
    const setting = this.deviceSetting[key];
    if (setting !== null && typeof setting !== 'string') {
      const availability = setting?.availability ?? false;
      return availability;
    }
    return false;
  }

  public hasSensingData(key: SensingType): boolean {
    if (key === 'battery') {
      return this.historyBatteryPercentage != null;
    }
    if (this.latestDeviceInfo[key]?.eventAt) {
      return true;
    }
    return false;
  }

  public isSensorOff(key: SensingType): boolean {
    if (key === 'battery') {
      return false;
    }
    const setting = this.deviceSetting[key];
    if (setting !== null && typeof setting !== 'string') {
      return setting?.availability === true && setting?.enabled === false;
    }
    return false;
  }

  /**
   * 遷移元画面に戻る。
   */
  public back(): void {
    this.router.navigate([this.previousPage], {
      queryParams: this.getShipmentInfoLinkPrams(),
    });
  }

  /** 遷移元に指定するクエリ文字列 */
  getShipmentInfoLinkPrams(): {
    [key: string]: string | string[];
  } {
    if (this.previousShipmentId) {
      const queryParams: { [key: string]: string | string[] } = {
        [SOURCE_PAGE_QUERY_PARAM]: SourcePage.DEVICE_INFO,
        shipmentId: this.previousShipmentId,
      };
      if (this.previousResultHistoryFrom) {
        queryParams['resultHistoryFrom'] = this.previousResultHistoryFrom;
      }
      if (this.previousResultHistoryTo) {
        queryParams['resultHistoryTo'] = this.previousResultHistoryTo;
      }

      return queryParams;
    }

    return {};
  }
}
